import React from 'react';
import camelcaseKeys from 'camelcase-keys';
import fetch from 'cross-fetch';
import { v4 as uuid } from 'uuid';

import { portalApi } from '../../../api';
import { findSelected } from '../../../components/DropdownMenu/util';
import { Statuses } from '../../../constants/statuses';
import { multiSelectInsuredsOptions } from '../../../pages/JobDetailsPage/components/AddInsuredsToProjectTray/MultiSelectInsureds/util';
import { statusOptions } from '../../../pages/JobDetailsPage/util';
import insuredKeys from '../../../services/models/insured/keys';
import recordKeys from '../../../services/models/record/keys';
import { arrayFilterAndMap, createMap, modifyArrayItemAt, removeArrayItemAt, replaceArrayItemAt } from '../../../utils/arrayUtil';
import { debounceAction } from '../../../utils/debounce';
import { toDepartmentOption } from '../../../utils/departmentUtil';
import { queryClient } from '../../../utils/queryClientUtil';
import { globalRequirementTemplateBuilder, jobDetailsSearch } from '../../reducer';
import { createErrorToast, createSuccessToast, createToast } from '../app';
import { selectCompanyId } from '../company';
import { selectInsured } from '../insured';
import { jobSearch } from '../jobSearch';
import { hideTray } from '../tray';

// Actions
const JOBS_GET_JOBS = 'JOBS_GET_JOBS';
const JOBS_GET_JOBS_SUCCESS = 'JOBS_GET_JOBS_SUCCESS';
const JOBS_GET_JOBS_FAILURE = 'JOBS_GET_JOBS_FAILURE';

export const getJobs = (insuredId, pathParamId = null, pageData = { pageNumber: 1, pageSize: 100 }, loading = true) => async (dispatch, getState) => {
  dispatch({ type: JOBS_GET_JOBS, pageData, pathParamId, loading });
  const filter = getState().jobs.insuredJobsFilter;

  try {
    const { data, ...pageInfo } = await portalApi.listInsuredJobs(insuredId, pageData, filter);
    dispatch({ type: JOBS_GET_JOBS_SUCCESS, data, pageInfo, pathParamId });
  } catch (error) {
    dispatch({
      type: JOBS_GET_JOBS_FAILURE,
      error
    });
  }
};

const JOBS_GET_ARCHIVED_JOBS = 'JOBS_GET_ARCHIVED_JOBS';

export const getArchivedJobs = (insuredId, pageData = { pageNumber: 1, pageSize: 50 }) => async dispatch => {
  dispatch({ type: JOBS_GET_ARCHIVED_JOBS, insuredId });

  await dispatch(updateInsuredJobsFilter({ archived: true }));
};

const JOBS_GET_ACTIVE_JOBS = 'JOBS_GET_ACTIVE_JOBS';

export const getActiveJobs = (insuredId, pathParamId = null, pageData = { pageNumber: 1, pageSize: 100 }) => async dispatch => {
  dispatch({ type: JOBS_GET_ACTIVE_JOBS, insuredId });

  await dispatch(updateInsuredJobsFilter({ active: true }, pathParamId, pageData));
};

const JOBS_SET_CURRENT_JOB = 'JOBS_SET_CURRENT_JOB';

export const setCurrentJob = jobId => dispatch => {
  dispatch({ type: JOBS_SET_CURRENT_JOB, jobId });
};

const JOBS_GET_CURRENT_JOB = 'JOBS_GET_CURRENT_JOB';
const JOBS_GET_CURRENT_JOB_SUCCESS = 'JOBS_GET_CURRENT_JOB_SUCCESS';
const JOBS_GET_CURRENT_JOB_FAILURE = 'JOBS_GET_CURRENT_JOB_FAILURE';

export const getCurrentJob = jobId => async dispatch => {
  dispatch({ type: JOBS_GET_CURRENT_JOB, jobId });

  try {
    const currentJob = await portalApi.getJob(jobId, { include: ['attachments'] });
    dispatch({ type: JOBS_GET_CURRENT_JOB_SUCCESS, currentJob });
  } catch (error) {
    dispatch({
      type: JOBS_GET_CURRENT_JOB_FAILURE,
      error
    });
  }
};

const JOBS_UPDATE_JOB = 'JOBS_UPDATE_JOB';
const JOBS_UPDATE_JOB_SUCCESS = 'JOBS_UPDATE_JOB_SUCCESS';
const JOBS_UPDATE_JOB_FAILURE = 'JOBS_UPDATE_JOB_FAILURE';

export const updateJob = (jobId, jobData) => async (dispatch, getState) => {
  const { jobProfilePage: currentJobProfile } = getState().jobs;

  dispatch({ type: JOBS_UPDATE_JOB, jobId, jobData });

  dispatch(createToast({
    title: 'Updating Job',
    description: 'This may take a few seconds...',
    duration: 60000
  }));

  try {
    const updatedJob = await portalApi.updateJob(jobId, jobData);
    dispatch({ type: JOBS_UPDATE_JOB_SUCCESS, updatedJob });

    dispatch(createSuccessToast({ title: 'Job Updated Successfully!' }));

    // invalidate queries for insured profile page to force refetch
    await queryClient.invalidateQueries(insuredKeys.all);

    // TODO: if department id is changed, get current department's updated projects
  } catch (error) {
    dispatch({ type: JOBS_UPDATE_JOB_FAILURE, error, currentJobProfile });

    dispatch(createErrorToast({ title: 'Uh oh, something went wrong.', error }));
  }
};

const JOBS_UPDATE_CONTRACT_NUMBER = 'JOBS_UPDATE_CONTRACT_NUMBER';
const JOBS_UPDATE_CONTRACT_NUMBER_SUCCESS = 'JOBS_UPDATE_CONTRACT_NUMBER_SUCCESS';
const JOBS_UPDATE_CONTRACT_NUMBER_FAILURE = 'JOBS_UPDATE_CONTRACT_NUMBER_FAILURE';

export const updateContractNumber = (jobId, contractNumber) => dispatch => {
  dispatch({ type: JOBS_UPDATE_CONTRACT_NUMBER, jobId, contractNumber });

  try {
    // TODO: portalAPI call here
    dispatch({ type: JOBS_UPDATE_CONTRACT_NUMBER_SUCCESS, jobId, contractNumber });
  } catch (error) {
    dispatch({ type: JOBS_UPDATE_CONTRACT_NUMBER_FAILURE, error });
  }
};

const JOBS_GET_JOB_STATUS = 'JOBS_GET_JOB_STATUS';
const JOBS_SET_JOB_STATUS = 'JOBS_SET_JOB_STATUS';
const JOBS_GET_JOB_STATUS_FAILURE = 'JOBS_GET_JOB_STATUS_FAILURE';

export const getCurrentJobStatus = () => async (dispatch, getState) => {
  const { insuredId } = selectInsured(getState());
  const currentJobId = selectCurrentJobId(getState());

  dispatch({ type: JOBS_GET_JOB_STATUS, insuredId, currentJobId });

  try {
    const { status } = await portalApi.getInsuredJobStatus(insuredId, currentJobId);
    dispatch(setJobStatus(currentJobId, status));
  } catch (error) {
    dispatch({ type: JOBS_GET_JOB_STATUS_FAILURE, error });
  }
};

export const setJobStatus = (jobId, status) => dispatch => {
  dispatch({ type: JOBS_SET_JOB_STATUS, status, jobId });
};

const JOBS_RESET_JOBS = 'JOBS_RESET_JOBS';

export const resetJobs = () => dispatch => {
  dispatch({ type: JOBS_RESET_JOBS });
};

const JOBS_RESET_CURRENT_JOB = 'JOBS_RESET_CURRENT_JOB';

export const resetCurrentJob = () => dispatch => {
  dispatch({ type: JOBS_RESET_CURRENT_JOB });
};

const JOBS_GET_JOB_STATS = 'JOBS_GET_JOB_STATS';
const JOBS_GET_JOB_STATS_SUCCESS = 'JOBS_GET_JOB_STATS_SUCCESS';
const JOBS_GET_JOB_STATS_FAILURE = 'JOBS_GET_JOB_STATS_FAILURE';

export const getJobStats = jobId => async dispatch => {
  try {
    const stats = await portalApi.getJobStats(jobId);
    dispatch({ type: JOBS_GET_JOB_STATS, jobId });
    dispatch({ type: JOBS_GET_JOB_STATS_SUCCESS, stats, jobId });
  } catch (error) {
    dispatch({ type: JOBS_GET_JOB_STATS_FAILURE, error });
  }
};

const JOBS_GET_JOB_SELECT_OPTIONS = 'JOBS_GET_JOB_SELECT_OPTIONS';

export const getJobSelectOptions = () => (dispatch, getState) => {
  const { departments, users } = getState().company;

  const departmentSelectOptions = departments && departments.map(toDepartmentOption);

  const ownerOptions = users && users.map(({ userFirstName, userLastName, userId, isActive }) => {
    if (isActive) {
      return {
        userFirstName,
        userLastName,
        label: `${userFirstName} ${userLastName}`,
        id: userId
      };
    }
  });

  const ownerSelectOptions = ownerOptions && [{ displayName: 'N/A', label: 'N/A', id: -1 }, ...ownerOptions];

  dispatch({
    type: JOBS_GET_JOB_SELECT_OPTIONS,
    ownerSelectOptions,
    departmentSelectOptions
  });
};

const JOBS_GET_JOB_PROFILE = 'JOBS_GET_JOB_PROFILE';
const JOBS_GET_JOB_PROFILE_SUCCESS = 'JOBS_GET_JOB_PROFILE_SUCCESS';
const JOBS_GET_JOB_PROFILE_FAILURE = 'JOBS_GET_JOB_PROFILE_FAILURE';

export const getJobProfile = jobId => async (dispatch, getState) => {
  dispatch({ type: JOBS_GET_JOB_PROFILE, jobId });
  const { departments, users } = getState().company;
  const allPublishedTemplates = globalRequirementTemplateBuilder.selectPublishedRequirementTemplates(getState());

  try {
    const job = await portalApi.getJob(jobId, { include: ['requirementTemplate'] });

    const jobStatusOptions = [
      {
        displayName: 'Active',
        id: Statuses.Active,
        label: 'Activate'
      },
      {
        displayName: 'Archived',
        id: Statuses.Archived,
        label: 'Archive'
      },
      {
        displayName: 'Deleted',
        id: Statuses.Deleted,
        label: 'Delete'
      }
    ];

    const departmentSelectOptions = departments && departments.map(department => ({
      displayName: department.departmentName,
      ...toDepartmentOption(department)
    }));

    const currentTemplate = allPublishedTemplates.data.length
            && findSelected(allPublishedTemplates.data, job.default_requirement_template_id);

    const jobDepartment = departmentSelectOptions.length
            && findSelected(departmentSelectOptions, job.department_id);

    const ownerOptions = users && users.map(({ userFirstName, userLastName, userId, isActive }) => {
      if (isActive) {
        return {
          userFirstName,
          userLastName,
          label: `${userFirstName} ${userLastName}`,
          displayName: `${userFirstName} ${userLastName}`,
          id: userId
        };
      }
    });
    const ownerSelectOptions = ownerOptions && [{ displayName: 'N/A', label: 'N/A', id: -1 }, ...ownerOptions];

    const jobOwner = (job.owner_id && ownerOptions)
            && findSelected(ownerOptions, job.owner_id);

    dispatch({
      type: JOBS_GET_JOB_PROFILE_SUCCESS,
      job,
      jobStatusOptions,
      departmentSelectOptions,
      ownerSelectOptions,
      jobDepartment,
      department: departments.find(d => d.departmentId === job.department_id),
      jobOwner,
      allPublishedTemplates,
      currentTemplate
    });
  } catch (error) {
    dispatch({
      type: JOBS_GET_JOB_PROFILE_FAILURE,
      error
    });
  }
};

const JOBS_UPDATE_CURRENT_OWNER = 'JOBS_UPDATE_CURRENT_OWNER';

export const updateCurrentOwner = ownerObject => dispatch => {
  dispatch({ type: JOBS_UPDATE_CURRENT_OWNER, ownerObject });
};

const JOBS_UPDATE_CURRENT_DEPARTMENT = 'JOBS_UPDATE_CURRENT_DEPARTMENT';

export const updateCurrentDepartment = department => dispatch => {
  dispatch({ type: JOBS_UPDATE_CURRENT_DEPARTMENT, department });
};

const JOBS_UPDATE_CURRENT_TEMPLATE = 'JOBS_UPDATE_CURRENT_TEMPLATE';

export const updateCurrentTemplate = templateObject => dispatch => {
  dispatch({ type: JOBS_UPDATE_CURRENT_TEMPLATE, templateObject });
};

const JOBS_LIST_INSURANCE_REQUIREMENTS_EXCEL_EXPORT = 'JOBS_LIST_INSURANCE_REQUIREMENTS_EXCEL_EXPORT';
const JOBS_LIST_INSURANCE_REQUIREMENTS_EXCEL_EXPORT_SUCCESS = 'JOBS_LIST_INSURANCE_REQUIREMENTS_EXCEL_EXPORT_SUCCESS';
const JOBS_LIST_INSURANCE_REQUIREMENTS_EXCEL_EXPORT_FAILURE = 'JOBS_LIST_INSURANCE_REQUIREMENTS_EXCEL_EXPORT_FAILURE';

export const exportListInsuranceRequirementsToExcel = (jobId, searchParams) => async dispatch => {
  dispatch({ type: JOBS_LIST_INSURANCE_REQUIREMENTS_EXCEL_EXPORT, jobId, searchParams });

  dispatch(createToast({
    title: 'Downloading excel report...',
    duration: 60000
  }));

  try {
    const { excelLink } = await portalApi.exportListInsuranceRequirementsExcel(jobId, searchParams);
    const link = document.createElement('a');
    link.href = excelLink;
    link.setAttribute('download', `Project ${jobId} - ${Date.now()}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    dispatch(createToast({ title: 'Download successful!', status: 'success' }));
    dispatch({ type: JOBS_LIST_INSURANCE_REQUIREMENTS_EXCEL_EXPORT_SUCCESS, excelLink });
  } catch (error) {
    dispatch({ type: JOBS_LIST_INSURANCE_REQUIREMENTS_EXCEL_EXPORT_FAILURE, error });

    dispatch(createToast({
      title: 'Something went wrong with your download',
      description: 'Please try again or contact support.',
      status: 'error'
    }));
  }
};

const JOBS_LIST_JOBS_PROJECT_NAME_ORDER = 'JOBS_LIST_JOBS_PROJECT_NAME_ORDER';

export const setListJobsFiltersProjectNameOrder = order => async dispatch => {
  const sortDirection = order === 'desc';
  dispatch({ type: JOBS_LIST_JOBS_PROJECT_NAME_ORDER, order });
  await dispatch(jobSearch.updateSortBy([{ id: 'name.keyword', desc: sortDirection }]));
};

const JOBS_LIST_JOBS_PROJECT_STATUS = 'JOBS_LIST_JOBS_PROJECT_STATUS';

export const setListJobsFiltersProjectStatus = statusName => async (dispatch, getState) => {
  const { projectStatusOptions } = getState().jobs.listJobsFilters;
  const { companyId } = getState().company;

  const selectedStatus = projectStatusOptions.find(status => status.id === statusName);

  if (selectedStatus.searchFilters.length) {
    const filterStatus = selectedStatus.searchFilters[0]?.status;

    dispatch(jobSearch.setBaseFilters([
      { key: 'company.id', value: companyId },
      { key: 'status.keyword', value: filterStatus }
    ]));
  } else {
    dispatch(jobSearch.setBaseFilters([
      { key: 'company.id', value: companyId }
    ]));
  }

  if (!selectedStatus.searchFilters.length) {
    dispatch(jobSearch.updateSortBy([
      { id: 'name', desc: false }
    ]));
  }

  dispatch(jobSearch.getPage());

  dispatch({ type: JOBS_LIST_JOBS_PROJECT_STATUS, selectedStatus });
};

const JOBS_LIST_JOBS_SET_DEPARTMENTS = 'JOBS_LIST_JOBS_SET_DEPARTMENTS';

export const setListJobsDepartmentMap = departments => dispatch => {
  dispatch({ type: JOBS_LIST_JOBS_SET_DEPARTMENTS, departments });
};

const JOBS_LIST_JOBS_CHECK_DEPARTMENT = 'JOBS_LIST_JOBS_CHECK_DEPARTMENT';

export const setListJobsFiltersCheckDepartment = departmentId => (dispatch, getState) => {
  const mappedDepartments = getState().jobs.listJobsFilters.departments;
  const alreadyChecked = mappedDepartments[departmentId].checked;
  const checkedDepartments = arrayFilterAndMap(
    Object.values(mappedDepartments),
    dept => (alreadyChecked ? (dept.checked && dept.departmentId !== departmentId) : (dept.checked)),
    dept => ({ departmentId: dept.departmentId })
  );

  const selected = alreadyChecked ? checkedDepartments : [...checkedDepartments, { departmentId }];
  dispatch(jobSearch.updateQuickFilters('department', selected));

  dispatch({ type: JOBS_LIST_JOBS_CHECK_DEPARTMENT, departmentId });
};

const JOBS_LIST_JOBS_CHECK_ALL_DEPARTMENTS = 'JOBS_LIST_JOBS_CHECK_ALL_DEPARTMENTS';

export const setListJobsFiltersCheckAllDepartments = value => async (dispatch, getState) => {
  const { departments } = getState().jobs.listJobsFilters;
  const updatedCheckboxArray = Object.values(departments).map(department => ({ ...department, checked: value }));
  const updatedDepartmentMap = createMap(updatedCheckboxArray, 'departmentId');

  const selected = value ? arrayFilterAndMap(
    updatedCheckboxArray,
    dept => (dept.checked),
    dept => ({ departmentId: dept.departmentId })
  ) : [{ departmentId: 0 }];

  dispatch(jobSearch.updateQuickFilters('department', selected));
  dispatch({ type: JOBS_LIST_JOBS_CHECK_ALL_DEPARTMENTS, updatedDepartmentMap });
};

const JOBS_LIST_JOBS_ADD_PROJECT_OWNER = 'JOBS_LIST_JOBS_ADD_PROJECT_OWNER';

export const setListJobsFiltersAddProjectOwner = user => (dispatch, getState) => {
  const { projectOwners } = getState().jobs.listJobsFilters;
  const selected = projectOwners.map(owner => ({ userId: owner.userId }));

  dispatch(jobSearch.updateQuickFilters('owner', [...selected, { userId: user.userId }]));
  dispatch({ type: JOBS_LIST_JOBS_ADD_PROJECT_OWNER, user });
};

const JOBS_LIST_JOBS_REMOVE_PROJECT_OWNER = 'JOBS_LIST_JOBS_REMOVE_PROJECT_OWNER';

export const setListJobsFiltersRemoveProjectOwner = user => (dispatch, getState) => {
  const { projectOwners } = getState().jobs.listJobsFilters;
  const selected = arrayFilterAndMap(
    projectOwners,
    owner => (owner.userId !== user.userId),
    owner => ({ userId: owner.userId })
  );

  dispatch(jobSearch.updateQuickFilters('owner', selected));
  dispatch({ type: JOBS_LIST_JOBS_REMOVE_PROJECT_OWNER, user });
};

const JOBS_LIST_JOBS_CLEAR_FILTERS = 'JOBS_LIST_JOBS_CLEAR_FILTERS';

export const clearListJobsFilters = () => async dispatch => {
  await dispatch(jobSearch.resetFilters());
  await dispatch(setListJobsFiltersProjectStatus('active'));
  await dispatch(setListJobsFiltersCheckAllDepartments(true));
  await dispatch(jobSearch.updateSortBy([{ id: 'name.keyword', desc: false }]));
  dispatch({ type: JOBS_LIST_JOBS_CLEAR_FILTERS });
};

const JOBS_LIST_UNMOUNT = 'JOBS_LIST_UNMOUNT';

export const unmountJobsListSearch = () => async dispatch => {
  dispatch(jobSearch.clearSearch());
  dispatch({ type: JOBS_LIST_UNMOUNT });
};

const JOBS_LIST_JOBS_EXPORT_EXCEL = 'JOBS_LIST_JOBS_EXPORT_EXCEL';
const JOBS_LIST_JOBS_EXPORT_EXCEL_SUCCESS = 'JOBS_LIST_JOBS_EXPORT_EXCEL_SUCCESS';
const JOBS_LIST_JOBS_EXPORT_EXCEL_FAILURE = 'JOBS_LIST_JOBS_EXPORT_EXCEL_FAILURE';

export const exportListJobsToExcel = (companyId, jobSearchParams) => async dispatch => {
  dispatch({ type: JOBS_LIST_JOBS_EXPORT_EXCEL, companyId });

  dispatch(createToast({ title: 'Downloading Excel Report' }));

  try {
    const { excelLink } = await portalApi.exportListJobsExcel(companyId, jobSearchParams);
    const link = document.createElement('a');
    link.href = excelLink;
    link.setAttribute('download', `Projects - ${Date.now()}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    dispatch({ type: JOBS_LIST_JOBS_EXPORT_EXCEL_SUCCESS, excelLink });

    dispatch(createSuccessToast({ title: 'Download Successful!' }));
  } catch (error) {
    dispatch({ type: JOBS_LIST_JOBS_EXPORT_EXCEL_FAILURE, error });

    dispatch(createErrorToast({ title: 'Something went wrong with your download.', error }));
  }

  return { success: true, message: '' };
};

const JOBS_CREATE_NEW_JOB = 'JOBS_CREATE_NEW_JOB';
const JOBS_CREATE_NEW_JOB_SUCCESS = 'JOBS_CREATE_NEW_JOB_SUCCESS';
const JOBS_CREATE_NEW_JOB_FAILURE = 'JOBS_CREATE_NEW_FAILURE';

export const createNewJob = jobData => async dispatch => {
  dispatch({ type: JOBS_CREATE_NEW_JOB, jobData });

  dispatch(createToast({
    title: 'Creating New Job',
    description: 'This might take a few seconds...',
    duration: 60000
  }));

  try {
    const newJob = await portalApi.createNewJob(jobData);

    dispatch({ type: JOBS_CREATE_NEW_JOB_SUCCESS, newJob });

    dispatch(createSuccessToast({ title: 'Job Created Successfully!' }));
    return { success: true, job: camelcaseKeys(newJob) };
  } catch (error) {
    dispatch({ type: JOBS_CREATE_NEW_JOB_FAILURE, error });

    dispatch(createErrorToast({ title: 'Oops, something went wrong...', error }));
    return { success: false, message: error.message };
  }
};

const JOBS_CREATE_ATTACHMENT = 'JOBS_CREATE_ATTACHMENT';
const JOBS_CREATE_ATTACHMENT_SUCCESS = 'JOBS_CREATE_ATTACHMENT_SUCCESS';
const JOBS_CREATE_ATTACHMENT_FAILURE = 'JOBS_CREATE_ATTACHMENT_FAILURE';

export const createJobAttachment = files => async (dispatch, getState) => {
  const { jobId } = selectCurrentJob(getState());

  files.forEach(async file => {
    const { name, size } = file;
    const tempId = uuid();

    dispatch({
      type: JOBS_CREATE_ATTACHMENT,
      attachment: {
        name,
        size,
        tempId,
        status: Statuses.Uploading
      }
    });

    try {
      const attachment = await portalApi.createJobAttachment({ name, size, jobId });
      attachment.status = Statuses.Uploading;
      await fetch(attachment.upload_url, { method: 'PUT', body: file });
      dispatch({ type: JOBS_CREATE_ATTACHMENT_SUCCESS, attachment, tempId });
      dispatch(pollJobAttachmentStatus(attachment.id));
    } catch (error) {
      dispatch({ type: JOBS_CREATE_ATTACHMENT_FAILURE, error, id: tempId });
      dispatch(createErrorToast({ title: 'Error creating attachment', description: error.message, error }));
    }
  });
};

const JOBS_POLL_ATTACHMENT_STATUS = 'JOBS_POLL_ATTACHMENT_STATUS';
const JOBS_UPLOAD_ATTACHMENT_SUCCESS = 'JOBS_UPLOAD_ATTACHMENT_SUCCESS';
const JOBS_UPLOAD_ATTACHMENT_FAILURE = 'JOBS_UPLOAD_ATTACHMENT_FAILURE';

export const pollJobAttachmentStatus = id => async dispatch => {
  let count = 0;

  const intervalId = setInterval(async () => {
    if (count > 30) {
      clearInterval(intervalId);
      dispatch({ type: JOBS_UPLOAD_ATTACHMENT_FAILURE, error: Error('attachment timed out'), id });
      dispatch(createErrorToast({
        title: 'Something went wrong while uploading your attachment.',
        description: 'Please contact support for assistance.'
      }));
    }

    try {
      const attachment = await portalApi.getAttachment(id);
      if (attachment.status === Statuses.Active) {
        dispatch({ type: JOBS_UPLOAD_ATTACHMENT_SUCCESS, attachment });
        clearInterval(intervalId);
      }

      if (attachment.status === Statuses.Error) {
        clearInterval(intervalId);
        dispatch({ type: JOBS_UPLOAD_ATTACHMENT_FAILURE, error: Error('AWS error uploading attachment'), id });
        dispatch(createErrorToast({
          title: 'There was an error uploading your attachment.',
          description: 'Please contact support for assistance.'
        }));
      }
    } catch (error) {
      dispatch({ type: JOBS_UPLOAD_ATTACHMENT_FAILURE, error: error.message, id });
      dispatch(createErrorToast({
        title: 'Something went wrong while uploading your attachment.',
        description: 'Please contact support for assistance.',
        error
      }));
    }

    count += 1;
  }, 1000);

  dispatch({ type: JOBS_POLL_ATTACHMENT_STATUS });
};

const JOBS_DELETE_ATTACHMENT = 'JOBS_DELETE_ATTACHMENT';
const JOBS_DELETE_ATTACHMENT_SUCCESS = 'JOBS_DELETE_ATTACHMENT_SUCCESS';
const JOBS_DELETE_ATTACHMENT_FAILURE = 'JOBS_DELETE_ATTACHMENT_FAILURE';

export const deleteJobAttachment = id => async dispatch => {
  dispatch({ type: JOBS_DELETE_ATTACHMENT, id });

  dispatch(createToast({ title: 'Deleting Attachment ' }));

  try {
    await portalApi.deleteAttachment(id);
    dispatch({ type: JOBS_DELETE_ATTACHMENT_SUCCESS, id });

    dispatch(createSuccessToast({ title: 'Attachment Deleted!' }));
  } catch (error) {
    dispatch({ type: JOBS_DELETE_ATTACHMENT_FAILURE, id, error: error.message });
    dispatch(createErrorToast({ title: 'Oops, something went wrong.' }));
  }
};

const JOBS_UPDATE_COMPANY_INSUREDS_TAB = 'JOBS_UPDATE_COMPANY_INSUREDS_TAB';

export const updateCompanyInsuredsTab = value => dispatch => {
  dispatch({ type: JOBS_UPDATE_COMPANY_INSUREDS_TAB, value });
};

const JOBS_GET_COMPANY_INSUREDS = 'JOBS_GET_COMPANY_INSUREDS';
const JOBS_GET_COMPANY_INSUREDS_SUCCESS = 'JOBS_GET_COMPANY_INSUREDS_SUCCESS';
const JOBS_GET_COMPANY_INSUREDS_FAILURE = 'JOBS_GET_COMPANY_INSUREDS_FAILURE';

export const getCompanyInsureds = (
  companyId,
  pageData = { pageSize: 25, pageNumber: 1 },
  filters = {},
  sort = '',
  includes = {}
) => async (dispatch, getState) => {
  dispatch({ type: JOBS_GET_COMPANY_INSUREDS, companyId, pageData, filters, sort, includes });

  try {
    const { data, ...pageInfo } = await portalApi.getCompanyInsureds(
      companyId ?? selectCompanyId(getState()),
      pageData,
      filters,
      sort,
      includes
    );
    dispatch({ type: JOBS_GET_COMPANY_INSUREDS_SUCCESS, data, pageInfo });
  } catch (error) {
    dispatch({ type: JOBS_GET_COMPANY_INSUREDS_FAILURE, error });
  }
};

const JOBS_UPDATE_COMPANY_INSUREDS_SEARCH_TERM = 'JOBS_UPDATE_COMPANY_INSUREDS_SEARCH_TERM';

export const updateCompanyInsuredsSearchTerm = debounceAction((value, filters = {}) => dispatch => {
  dispatch({ type: JOBS_UPDATE_COMPANY_INSUREDS_SEARCH_TERM, value });
  dispatch(getCompanyInsureds(
    null,
    { pageSize: 25, pageNumber: 1 },
    { insured_company_name: value, ...filters }
  ));
});

const JOBS_SELECT_COMPANY_INSURED = 'JOBS_SELECT_COMPANY_INSURED';

export const checkCompanyInsured = (insured, isChecked) => dispatch => {
  dispatch({ type: JOBS_SELECT_COMPANY_INSURED, insured, isChecked });
};

const JOBS_ADD_NEW_INSURED_TO_COMPANY_LIST = 'JOBS_ADD_NEW_INSURED_TO_COMPANY_LIST';

export const addNewInsuredToCompanyList = insured => dispatch => {
  dispatch(updateCompanyInsuredsTab(multiSelectInsuredsOptions.search.key));
  dispatch({ type: JOBS_ADD_NEW_INSURED_TO_COMPANY_LIST, insured });
};

const JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS = 'JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS';
const JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS_SUCCESS = 'JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS_SUCCESS';
const JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS_FAILURE = 'JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS_FAILURE';

export const bulkCreateInsuranceRequirements = (jobId, insuredIds, requirementTemplateId) => async dispatch => {
  dispatch({ type: JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS, jobId, insuredIds, requirementTemplateId });

  dispatch(createToast({
    title: 'Adding insureds to project...',
    description: 'This may take a few seconds',
    duration: 60000
  }));

  try {
    const {
      added_insureds: addedInsureds
    } = await portalApi.bulkCreateInsuranceRequirements(jobId, insuredIds, requirementTemplateId);

    dispatch({ type: JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS_SUCCESS, addedInsureds });

    if (addedInsureds.length) {
      dispatch(createSuccessToast({
        title: `Added ${addedInsureds.length} insured${addedInsureds.length > 1 ? 's' : ''} to your project.`,
        description: `You'll see ${insuredIds.length > 1 ? 'them' : 'it'} momentarily.`
      }));

      dispatch(jobDetailsSearch.getPage());
      dispatch(getJobStats(jobId));
      dispatch(hideTray());
    } else {
      dispatch(createErrorToast({ title: 'No insureds were added to your project' }));
    }
  } catch (error) {
    dispatch({ type: JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS_FAILURE, error });

    dispatch(createErrorToast({
      title: 'Oops, something went wrong',
      description: 'Please contact support for assistance.',
      error
    }));
  }
};

const JOBS_SET_BUTTON_PULSE = 'JOBS_SET_BUTTON_PULSE';

export const setJobsButtonPulse = bool => dispatch => {
  dispatch({ type: JOBS_SET_BUTTON_PULSE, bool });
};

const JOBS_RESET_ADD_INSURED_TO_PROJECT = 'JOBS_RESET_ADD_INSURED_TO_PROJECT';

export const resetAddInsuredToProject = () => dispatch => {
  dispatch({ type: JOBS_RESET_ADD_INSURED_TO_PROJECT });
};

const JOBS_CURRENTLY_CREATING_INSURED = 'JOBS_CURRENTLY_CREATING_INSURED';
export const setCurrentlyCreatingInsured = currentlyCreating => dispatch => {
  dispatch({ type: JOBS_CURRENTLY_CREATING_INSURED, currentlyCreating });
};

const JOBS_ARCHIVE_JOB = 'JOBS_ARCHIVE_JOB';
const JOBS_ARCHIVE_JOB_SUCCESS = 'JOBS_ARCHIVE_JOB_SUCCESS';
const JOBS_ARCHIVE_JOB_FAILURE = 'JOBS_ARCHIVE_JOB_FAILURE';

export const archiveJob = jobId => async dispatch => {
  dispatch({ type: JOBS_ARCHIVE_JOB, jobId });

  dispatch(createToast({
    title: 'Archiving Project',
    description: 'This might take a few seconds...',
    duration: 60000
  }));

  try {
    const response = await portalApi.archiveJob(jobId);

    dispatch({ type: JOBS_ARCHIVE_JOB_SUCCESS, job: response });
    dispatch(getJobStats(jobId));
    await dispatch(jobDetailsSearch.setBaseFilters([
      { key: 'job.id', value: jobId },
      { key: 'status.keyword', value: 'archived' }
    ]));

    dispatch(jobDetailsSearch.getPage());

    // invalidate queries for insured profile page to force refetch
    await queryClient.invalidateQueries(insuredKeys.all);

    dispatch(createSuccessToast({ title: 'Project Archived Successfully' }));
  } catch (error) {
    dispatch({ type: JOBS_ARCHIVE_JOB_FAILURE, error });

    dispatch(createErrorToast({ title: 'Uh oh, something went wrong.', error }));
  }
};

const JOBS_REACTIVATE_JOB = 'JOBS_REACTIVATE_JOB';
const JOBS_REACTIVATE_JOB_SUCCESS = 'JOBS_REACTIVATE_JOB_SUCCESS';
const JOBS_REACTIVATE_JOB_FAILURE = 'JOBS_REACTIVATE_JOB_FAILURE';

export const reactivateJob = jobId => async dispatch => {
  dispatch({ type: JOBS_REACTIVATE_JOB, jobId });

  dispatch(createToast({
    title: 'Activating Project',
    description: 'This might take a few seconds...',
    duration: 60000
  }));

  try {
    const response = await portalApi.reactivateJob(jobId);
    dispatch({ type: JOBS_REACTIVATE_JOB_SUCCESS, job: response });
    dispatch(getJobStats(jobId));
    await dispatch(jobDetailsSearch.setBaseFilters([
      { key: 'job.id', value: jobId },
      { key: 'status.keyword', value: ['approved', 'not_approved'] }
    ]));

    dispatch(jobDetailsSearch.getPage());

    // invalidate queries for insured profile page to force refetch
    await queryClient.invalidateQueries(insuredKeys.all);

    dispatch(createSuccessToast({ title: 'Project Activated Successfully' }));
  } catch (error) {
    dispatch({ type: JOBS_REACTIVATE_JOB_FAILURE });
    dispatch(createErrorToast({ title: 'Uh oh. Something went wrong.', error }));
  }
};

const JOBS_DELETE_JOB = 'JOBS_DELETE_JOB';
const JOBS_DELETE_JOB_SUCCESS = 'JOBS_DELETE_JOB_SUCCESS';
const JOBS_DELETE_JOB_FAILURE = 'JOBS_DELETE_JOB_FAILURE';

export const deleteJob = jobId => async dispatch => {
  dispatch({ type: JOBS_DELETE_JOB, jobId });

  try {
    await portalApi.deleteJob(jobId);

    dispatch({ type: JOBS_DELETE_JOB_SUCCESS });

    // invalidate queries for insured profile page to force refetch
    await queryClient.invalidateQueries(insuredKeys.all);

    dispatch(createSuccessToast({ title: 'Project Successfully Deleted' }));
  } catch (error) {
    dispatch({ type: JOBS_DELETE_JOB_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: 'Project was not deleted',
      error
    }));
  }
};

const JOBS_ARCHIVE_INSURANCE_REQUIREMENT = 'JOBS_ARCHIVE_INSURANCE_REQUIREMENT';
const JOBS_ARCHIVE_INSURANCE_REQUIREMENT_SUCCESS = 'JOBS_ARCHIVE_INSURANCE_REQUIREMENT_SUCCESS';
const JOBS_ARCHIVE_INSURANCE_REQUIREMENT_FAILURE = 'JOBS_ARCHIVE_INSURANCE_REQUIREMENT_FAILURE';

export const archiveInsuranceRequirement = recordId => async dispatch => {
  dispatch({ type: JOBS_ARCHIVE_INSURANCE_REQUIREMENT, recordId });

  try {
    const response = await portalApi.archiveRecord(recordId);

    dispatch({ type: JOBS_ARCHIVE_INSURANCE_REQUIREMENT_SUCCESS, jobId: response.job_id });
    dispatch(createSuccessToast({ title: 'Record Successfully Archived' }));
    dispatch(jobDetailsSearch.getPage());
    dispatch(getJobStats(response.job_id));

    // invalidate queries for insured profile page to force refetch
    queryClient.invalidateQueries(insuredKeys.insuredAllRecordQueriesKey(response.insured_id));
    queryClient.invalidateQueries(insuredKeys.insuredOverallStatus(response.insured_id));

    return { success: true };
  } catch (error) {
    dispatch({ type: JOBS_ARCHIVE_INSURANCE_REQUIREMENT_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: 'Record was not archived',
      error
    }));

    return { success: false };
  }
};

const JOBS_REACTIVATE_INSURANCE_REQUIREMENT = 'JOBS_REACTIVATE_INSURANCE_REQUIREMENT';
const JOBS_REACTIVATE_INSURANCE_REQUIREMENT_SUCCESS = 'JOBS_REACTIVATE_INSURANCE_REQUIREMENT_SUCCESS';
const JOBS_REACTIVATE_INSURANCE_REQUIREMENT_FAILURE = 'JOBS_REACTIVATE_INSURANCE_REQUIREMENT_FAILURE';

export const reactivateInsuranceRequirement = recordId => async dispatch => {
  dispatch({ type: JOBS_REACTIVATE_INSURANCE_REQUIREMENT, recordId });

  try {
    const response = await portalApi.reactivateRecord(recordId);

    dispatch({ type: JOBS_REACTIVATE_INSURANCE_REQUIREMENT_SUCCESS });
    dispatch(createSuccessToast({ title: 'Record Successfully Reactivated' }));
    dispatch(jobDetailsSearch.getPage());
    dispatch(getJobStats(response.job_id));

    // invalidate queries for insured profile page to force refetch
    queryClient.invalidateQueries(insuredKeys.insuredAllRecordQueriesKey(response.insured_id));
    queryClient.invalidateQueries(insuredKeys.insuredOverallStatus(response.insured_id));
    queryClient.invalidateQueries(recordKeys.recordPolicies(recordId));
    queryClient.invalidateQueries(recordKeys.recordDocuments(recordId));
    queryClient.invalidateQueries(recordKeys.recordPolicyRequirements(recordId));
    queryClient.invalidateQueries(recordKeys.recordItemRequirements(recordId));
    queryClient.invalidateQueries(recordKeys.recordDocumentRequirements(recordId));
    queryClient.invalidateQueries(recordKeys.recordQuestionRequirements(recordId));
    queryClient.invalidateQueries(recordKeys.recordLanguageRequirements(recordId));

    return { success: true };
  } catch (error) {
    dispatch({ type: JOBS_REACTIVATE_INSURANCE_REQUIREMENT_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: 'Record was not reactivated',
      error
    }));

    return { success: false };
  }
};

const JOBS_DELETE_INSURANCE_REQUIREMENT = 'JOBS_DELETE_INSURANCE_REQUIREMENT';
const JOBS_DELETE_INSURANCE_REQUIREMENT_SUCCESS = 'JOBS_DELETE_INSURANCE_REQUIREMENT_SUCCESS';
const JOBS_DELETE_INSURANCE_REQUIREMENT_FAILURE = 'JOBS_DELETE_INSURANCE_REQUIREMENT_FAILURE';

export const deleteInsuranceRequirement = recordId => async (dispatch, getState) => {
  dispatch({ type: JOBS_DELETE_INSURANCE_REQUIREMENT, recordId });
  const currentJob = selectCurrentJobId(getState());

  try {
    await portalApi.deleteRecord(recordId);

    // invalidate queries for insured profile page to force refetch
    await queryClient.invalidateQueries(insuredKeys.all);

    dispatch({ type: JOBS_DELETE_INSURANCE_REQUIREMENT_SUCCESS });
    dispatch(createSuccessToast({ title: 'Record Successfully Deleted' }));
    dispatch(jobDetailsSearch.getPage());
    dispatch(getJobStats(currentJob));

    return { success: true };
  } catch (error) {
    dispatch({ type: JOBS_DELETE_INSURANCE_REQUIREMENT_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong',
      description: 'Record was not deleted',
      error
    }));

    return { success: false };
  }
};

const JOBS_UPDATE_INSURED_JOBS_FILTER = 'JOBS_UPDATE_INSURED_JOBS_FILTER';
export const updateInsuredJobsFilter = (filter, pathParamId = null, pageData = { pageNumber: 1, pageSize: 100 }) => (dispatch, getState) => {
  const { insuredId } = selectInsured(getState());
  dispatch({ type: JOBS_UPDATE_INSURED_JOBS_FILTER, filter });
  dispatch(getJobs(insuredId, pathParamId, pageData));
};

// Reducer
const DEFAULT_STATE = {
  currentJob: null,
  currentJobId: null,
  updatedJob: null,
  jobs: {
    all: {
      data: [],
      pageInfo: null
    },
    active: {
      data: [],
      pageInfo: null
    },
    archived: {
      data: [],
      pageInfo: null
    }
  },
  insuredJobsFilter: {},
  jobsListLoading: false,
  jobProfilePage: null,
  currentlyUpdatingJob: false,
  currentlyCreatingJob: false,
  currentlyAddingInsuredToJob: false,
  currentlyCreatingInsured: false,
  listJobsFilters: {
    projectNameOrder: 'asc',
    projectStatusOptions: [
      {
        id: 'active',
        displayName: 'Active Projects',
        searchFilters: [{ status: 'active' }]
      },
      {
        id: 'archived',
        displayName: 'Archived Projects',
        searchFilters: [{ status: 'archived' }]
      },
      {
        id: 'all',
        displayName: 'All Projects',
        searchFilters: []
      }
    ],
    selectedProjectStatus: {
      id: 'active',
      displayName: 'Active Projects',
      searchFilters: [{ status: 'active' }]
    },
    departments: {},
    projectOwners: []
  },
  jobSelectOptions: {},
  companyInsuredsList: {
    searchTerm: '',
    data: [],
    pageInfo: null,
    selected: [],
    currentTab: multiSelectInsuredsOptions.search.key,
    buttonPulse: false,
    isLoading: true
  }
};

const jobsReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case JOBS_GET_JOBS:
      return { ...state, jobsListLoading: action.loading };

    case JOBS_GET_JOBS_SUCCESS: {
      const filter = Object.keys(state.insuredJobsFilter);
      const jobFilter = !filter.length ? 'all' : filter[0];

      const updatedJobs = action.pageInfo.currentPage > 1
        ? [...state.jobs[jobFilter].data, ...action.data]
        : action.data;

      if (!state.currentJobId && jobFilter === Statuses.Active) {
        const currentJob = action.pathParamId ? action.data.find(job => job.jobId === action.pathParamId) : (action.data[0] ?? null);

        return {
          ...state,
          jobs: {
            ...state.jobs,
            [jobFilter]: {
              data: updatedJobs,
              pageInfo: action.pageInfo
            }
          },
          jobsListLoading: false,
          currentJob,
          currentJobId: currentJob ? currentJob.jobId : null
        };
      }

      return {
        ...state,
        jobs: {
          ...state.jobs,
          [jobFilter]: {
            data: updatedJobs,
            pageInfo: action.pageInfo
          }
        },
        jobsListLoading: false
      };
    }

    case JOBS_GET_JOBS_FAILURE:
      return { ...state, error: action.error, jobsListLoading: false };

    case JOBS_SET_CURRENT_JOB: {
      const currentJobId = parseInt(action.jobId, 10);

      return {
        ...state,
        currentJobId,
        currentJob: state.jobs.active.data.find(job => job.jobId === currentJobId)
      };
    }

    case JOBS_GET_CURRENT_JOB_SUCCESS:
      return { ...state, currentJob: action.currentJob, currentJobId: action.currentJob.jobId };

    case JOBS_GET_CURRENT_JOB_FAILURE:
      return { ...state, error: action.error };

    case JOBS_UPDATE_JOB:
      return {
        ...state,
        updatedJob: action.jobData,
        jobProfilePage: {
          ...state.jobProfilePage,
          ...action.jobData
        },
        currentlyUpdatingJob: true
      };

    case JOBS_UPDATE_JOB_SUCCESS:
      return {
        ...state,
        updatedJob: action.updatedJob,
        jobProfilePage: {
          ...state.jobProfilePage,
          ...action.updatedJob,
          currentRequirements: action.updatedJob.requirementTemplate
        },
        currentlyUpdatingJob: false
      };

    case JOBS_UPDATE_JOB_FAILURE:
      return {
        ...state,
        error: action.error,
        jobProfilePage: {
          ...state.jobProfilePage,
          ...action.currentJobProfile
        },
        currentlyUpdatingJob: false
      };

    case JOBS_CREATE_ATTACHMENT:
      return {
        ...state,
        currentJob: {
          ...state.currentJob,
          attachments: [...state.currentJob.attachments, action.attachment]
        }
      };

    case JOBS_CREATE_ATTACHMENT_SUCCESS:
      return {
        ...state,
        currentJob: {
          ...state.currentJob,
          attachments: replaceArrayItemAt(
            state.currentJob.attachments,
            'tempId',
            action.tempId,
            action.attachment
          )
        }
      };

    case JOBS_CREATE_ATTACHMENT_FAILURE:
      return {
        ...state,
        currentJob: {
          ...state.currentJob,
          attachments: removeArrayItemAt(
            state.currentJob.attachments,
            'id',
            action.id
          )
        }
      };

    case JOBS_UPLOAD_ATTACHMENT_SUCCESS:
      return {
        ...state,
        currentJob: {
          ...state.currentJob,
          attachments: replaceArrayItemAt(
            state.currentJob.attachments,
            'id',
            action.attachment.id,
            action.attachment
          )
        }
      };

    case JOBS_UPLOAD_ATTACHMENT_FAILURE:
      return {
        ...state,
        currentJob: {
          ...state.currentJob,
          attachments: removeArrayItemAt(
            state.currentJob.attachments,
            'id',
            action.id
          )
        }
      };

    case JOBS_DELETE_ATTACHMENT_SUCCESS:
      return {
        ...state,
        currentJob: {
          ...state.currentJob,
          attachments: removeArrayItemAt(state.currentJob.attachments, 'id', action.id)
        }
      };

    case JOBS_UPDATE_CONTRACT_NUMBER: {
      const jobsPage = state.jobs.active;

      const updatedJobs = modifyArrayItemAt(
        jobsPage.data,
        'jobId',
        action.jobId,
        element => ({ ...element, contractNumber: action.contractNumber })
      );

      return { ...state, jobs: { ...state.jobs, active: { ...state.jobs.active, data: updatedJobs } } };
    }

    case JOBS_UPDATE_CONTRACT_NUMBER_SUCCESS:
      return { ...state };

    case JOBS_UPDATE_CONTRACT_NUMBER_FAILURE:
      return { ...state, error: action.error };

    case JOBS_SET_JOB_STATUS: {
      const jobsPage = state.jobs.active;

      const updatedJobStatuses = modifyArrayItemAt(
        jobsPage.data,
        'jobId',
        action.jobId,
        job => ({ ...job, requirementStatus: action.status })
      );
      return { ...state, jobs: { ...state.jobs, active: { ...state.jobs.active, data: updatedJobStatuses } } };
    }

    case JOBS_GET_JOB_STATUS_FAILURE:
      return { ...state, error: action.error };

    case JOBS_GET_JOB_STATS_SUCCESS: {
      return {
        ...state,
        currentJob: { ...state.currentJob, stats: action.stats },
        jobProfilePage: { ...state.jobProfilePage, stats: action.stats }
      };
    }

    case JOBS_GET_JOB_STATS_FAILURE: {
      return { ...state, error: action.error };
    }

    case JOBS_RESET_JOBS: {
      const defaultState = {
        ...DEFAULT_STATE,
        listJobsFilters: state.listJobsFilters,
        currentlyUpdatingJob: state.currentlyUpdatingJob,
        currentlyCreatingJob: state.currentlyCreatingJob,
        currentlyAddingInsuredToJob: state.currentlyAddingInsuredToJob
      };

      if (state.currentJob) {
        const currentJobAttachments = state.currentJob?.attachments ?? [];

        if (currentJobAttachments.some(attachment => [Statuses.Created, Statuses.Uploading].includes((attachment?.status)))) {
          defaultState.currentJob = { attachments: state.currentJob.attachments };
        }
      }

      return defaultState;
    }

    case JOBS_RESET_CURRENT_JOB:
      return { ...state, currentJob: null, currentJobId: null };

    case JOBS_GET_JOB_PROFILE:
      return state;
    case JOBS_GET_JOB_PROFILE_SUCCESS:
      return {
        ...state,
        jobProfilePage: {
          ...state.jobProfilePage,
          ...action.job,
          jobStatusOptions: action.jobStatusOptions,
          jobOwnerOptions: action.ownerSelectOptions,
          currentOwner: action.jobOwner,
          departmentOptions: action.departmentSelectOptions,
          currentDepartment: action.jobDepartment,
          requirementOptions: action.allPublishedTemplates,
          currentRequirements: action.job.requirementTemplate ?? action.currentTemplate,
          department: action.department
        }
      };

    case JOBS_ARCHIVE_JOB:
      return { ...state, currentlyUpdatingJob: true };

    case JOBS_ARCHIVE_JOB_SUCCESS:
      return {
        ...state,
        currentlyUpdatingJob: false,
        currentJob: action.job,
        jobProfilePage: { ...state.jobProfilePage, ...action.job }
      };

    case JOBS_ARCHIVE_JOB_FAILURE:
      return { ...state, currentlyUpdatingJob: false };

    case JOBS_REACTIVATE_JOB:
      return { ...state, currentlyUpdatingJob: true };

    case JOBS_REACTIVATE_JOB_SUCCESS:
      return {
        ...state,
        currentlyUpdatingJob: false,
        currentJob: action.job,
        jobProfilePage: { ...state.jobProfilePage, ...action.job }
      };

    case JOBS_REACTIVATE_JOB_FAILURE:
      return { ...state, currentlyUpdatingJob: false };

    case JOBS_DELETE_JOB:
      return { ...state, currentlyUpdatingJob: true };

    case JOBS_DELETE_JOB_SUCCESS:
      return { ...state, currentlyUpdatingJob: false };

    case JOBS_DELETE_JOB_FAILURE:
      return { ...state, currentlyUpdatingJob: false };

    case JOBS_ARCHIVE_INSURANCE_REQUIREMENT:
      return { ...state, currentlyUpdatingJob: true };

    case JOBS_ARCHIVE_INSURANCE_REQUIREMENT_SUCCESS: {
      const updatedCurrentJob = (state.currentJob.jobId === action.jobId && state.jobs.active.data.length)
        ? state.jobs.active.data[0] : null;
      return {
        ...state,
        currentlyUpdatingJob: false,
        ...(!state.jobProfilePage && { currentJob: updatedCurrentJob, currentJobId: updatedCurrentJob?.jobId || null })
      };
    }

    case JOBS_ARCHIVE_INSURANCE_REQUIREMENT_FAILURE:
      return { ...state, currentlyUpdatingJob: false };

    case JOBS_REACTIVATE_INSURANCE_REQUIREMENT:
      return { ...state, currentlyUpdatingJob: true };

    case JOBS_REACTIVATE_INSURANCE_REQUIREMENT_SUCCESS:
      return { ...state, currentlyUpdatingJob: false };

    case JOBS_REACTIVATE_INSURANCE_REQUIREMENT_FAILURE:
      return { ...state, currentlyUpdatingJob: false };

    case JOBS_DELETE_INSURANCE_REQUIREMENT:
      return { ...state, currentlyUpdatingJob: true };

    case JOBS_DELETE_INSURANCE_REQUIREMENT_SUCCESS:
      return { ...state, currentlyUpdatingJob: false };

    case JOBS_DELETE_INSURANCE_REQUIREMENT_FAILURE:
      return { ...state, currentlyUpdatingJob: false };

    case JOBS_UPDATE_CURRENT_OWNER:
      return {
        ...state,
        jobProfilePage: {
          ...state.jobProfilePage,
          currentOwner: action.ownerObject
        }
      };

    case JOBS_UPDATE_CURRENT_DEPARTMENT: {
      const { department } = action;
      return {
        ...state,
        jobProfilePage: {
          ...state.jobProfilePage,
          department,
          departmentId: department.id,
          currentDepartment: {
            displayName: department.label,
            label: department.label,
            id: department.value
          }
        }
      };
    }

    case JOBS_UPDATE_CURRENT_TEMPLATE:
      return {
        ...state,
        jobProfilePage: {
          ...state.jobProfilePage,
          currentRequirements: action.templateObject
        }
      };

    case JOBS_GET_JOB_SELECT_OPTIONS: {
      return {
        ...state,
        jobSelectOptions: {
          ownerOptions: action.ownerSelectOptions,
          departmentOptions: action.departmentSelectOptions,
          requirementTemplateOptions: action.allPublishedTemplates
        }
      };
    }

    case JOBS_CREATE_NEW_JOB:
      return {
        ...state,
        currentlyCreatingJob: true
      };

    case JOBS_CREATE_NEW_JOB_SUCCESS: {
      return {
        ...state,
        newJob: action.newJob,
        currentlyCreatingJob: false
      };
    }

    case JOBS_CREATE_NEW_JOB_FAILURE: {
      return {
        ...state,
        error: action.error,
        currentlyCreatingJob: false
      };
    }

    case JOBS_LIST_JOBS_PROJECT_NAME_ORDER:
      return { ...state, listJobsFilters: { ...state.listJobsFilters, projectNameOrder: action.order } };

    case JOBS_LIST_JOBS_PROJECT_STATUS:
      return {
        ...state,
        listJobsFilters: {
          ...state.listJobsFilters,
          selectedProjectStatus: action.selectedStatus
        }
      };

    case JOBS_LIST_JOBS_SET_DEPARTMENTS:
      return {
        ...state,
        listJobsFilters: { ...state.listJobsFilters, departments: action.departments }
      };

    case JOBS_LIST_JOBS_CHECK_DEPARTMENT:
      return {
        ...state,
        listJobsFilters: {
          ...state.listJobsFilters,
          departments: {
            ...state.listJobsFilters.departments,
            [action.departmentId]: {
              ...state.listJobsFilters.departments[action.departmentId],
              checked: !state.listJobsFilters.departments[action.departmentId].checked
            }
          }
        }
      };

    case JOBS_LIST_JOBS_CHECK_ALL_DEPARTMENTS:
      return {
        ...state,
        listJobsFilters: {
          ...state.listJobsFilters,
          departments: action.updatedDepartmentMap
        }
      };

    case JOBS_LIST_JOBS_ADD_PROJECT_OWNER: {
      if (state.listJobsFilters.projectOwners.some(user => user.userId === action.user.userId)) {
        return { ...state };
      }

      return {
        ...state,
        listJobsFilters: {
          ...state.listJobsFilters,
          projectOwners: [
            ...state.listJobsFilters.projectOwners,
            action.user
          ]
        }
      };
    }

    case JOBS_LIST_JOBS_REMOVE_PROJECT_OWNER: {
      const updatedProjectOwners = state.listJobsFilters.projectOwners
        .filter(user => user.userId !== action.user.userId);

      return {
        ...state,
        listJobsFilters: {
          ...state.listJobsFilters,
          projectOwners: updatedProjectOwners
        }
      };
    }

    case JOBS_LIST_JOBS_CLEAR_FILTERS: {
      return {
        ...state,
        listJobsFilters: {
          ...DEFAULT_STATE.listJobsFilters,
          departments: state.listJobsFilters.departments
        }
      };
    }

    case JOBS_UPDATE_COMPANY_INSUREDS_TAB:
      return {
        ...state,
        companyInsuredsList: {
          ...state.companyInsuredsList,
          currentTab: action.value
        }
      };

    case JOBS_GET_COMPANY_INSUREDS: {
      return {
        ...state,
        companyInsuredsList: {
          ...state.companyInsuredsList,
          isLoading: !state.companyInsuredsList?.pageInfo?.currentPage
        }
      };
    }

    case JOBS_GET_COMPANY_INSUREDS_SUCCESS: {
      const updatedData = action.pageInfo.currentPage > 1
        ? [...state.companyInsuredsList.data, ...action.data]
        : action.data;

      return {
        ...state,
        companyInsuredsList: {
          ...state.companyInsuredsList,
          data: updatedData,
          pageInfo: action.pageInfo,
          isLoading: false
        }
      };
    }

    case JOBS_UPDATE_COMPANY_INSUREDS_SEARCH_TERM:
      return {
        ...state,
        companyInsuredsList: {
          ...state.companyInsuredsList,
          searchTerm: action.value
        }
      };

    case JOBS_SELECT_COMPANY_INSURED: {
      const updatedSelected = action.isChecked
        ? [...state.companyInsuredsList.selected, action.insured]
        : state.companyInsuredsList.selected.filter(insured => action.insured.insuredId !== insured.insuredId);

      return {
        ...state,
        companyInsuredsList: {
          ...state.companyInsuredsList,
          selected: updatedSelected
        }
      };
    }

    case JOBS_ADD_NEW_INSURED_TO_COMPANY_LIST:
      return {
        ...state,
        companyInsuredsList: {
          ...state.companyInsuredsList,
          selected: [action.insured, ...state.companyInsuredsList.selected]
        }
      };

    case JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS:
      return {
        ...state,
        currentlyAddingInsuredToJob: true
      };

    case JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS_SUCCESS:
      return {
        ...state,
        currentlyAddingInsuredToJob: false
      };

    case JOBS_BULK_CREATE_INSURANCE_REQUIREMENTS_FAILURE:
      return {
        ...state,
        currentlyAddingInsuredToJob: false
      };

    case JOBS_SET_BUTTON_PULSE:
      return {
        ...state,
        companyInsuredsList: {
          ...state.companyInsuredsList,
          buttonPulse: action.bool
        }
      };

    case JOBS_RESET_ADD_INSURED_TO_PROJECT:
      return {
        ...state,
        companyInsuredsList: DEFAULT_STATE.companyInsuredsList
      };

    case JOBS_CURRENTLY_CREATING_INSURED:
      return {
        ...state,
        currentlyCreatingInsured: action.currentlyCreating
      };

    case JOBS_LIST_UNMOUNT: {
      return {
        ...state,
        listJobsFilters: { ...DEFAULT_STATE.listJobsFilters }
      };
    }

    case JOBS_UPDATE_INSURED_JOBS_FILTER: {
      return { ...state, insuredJobsFilter: action.filter };
    }

    default:
      return state;
  }
};

// Selectors
export const selectAllJobs = state => state.jobs.jobs.all.data;
export const selectActiveJobs = state => state.jobs.jobs.active.data;
export const selectArchivedJobs = state => state.jobs.jobs.archived.data;
export const selectJobsPageData = filter => state => state.jobs.jobs[filter].pageInfo;
export const selectInsuredJobFilter = state => Object.keys(state.jobs.insuredJobsFilter);
export const selectCurrentJob = state => state.jobs.currentJob;
export const selectJobsListLoading = state => state.jobs.jobsListLoading;
export const selectCurrentJobAttachments = state => state.jobs.currentJob.attachments;
export const selectUpdatedJob = state => state.jobs.updatedJob;
export const selectCurrentJobId = state => state.jobs.currentJobId;
export const selectJobProfile = state => state.jobs.jobProfilePage;
export const selectListJobsFilters = state => state.jobs.listJobsFilters;
export const selectJobSelectOptions = state => state.jobs.jobSelectOptions;
export const selectCompanyInsuredsList = state => state.jobs.companyInsuredsList;
export const selectCurrentlyUpdatingJob = state => state.jobs.currentlyUpdatingJob;
export const selectCurrentlyCreatingJob = state => state.jobs.currentlyCreatingJob;
export const selectCurrentlyAddingInsuredToJob = state => state.jobs.currentlyAddingInsuredToJob;
export const selectCurrentlyCreatingInsured = state => state.jobs.currentlyCreatingInsured;

export default jobsReducer;
